import { useState, useEffect } from 'react';

export interface AudioDevice {
  deviceId: string;
  label: string;
  kind: 'audioinput' | 'audiooutput';
}

export function useAudioDevices(enabled: boolean = true) {
  const [inputDevices, setInputDevices] = useState<AudioDevice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let mounted = true;

    const enumerateDevices = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Request microphone permission to get device labels
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach((track) => track.stop());
        } catch {
          console.warn('[useAudioDevices] Microphone permission not granted, device labels may be limited');
        }

        const devices = await navigator.mediaDevices.enumerateDevices();

        if (!mounted) return;

        const inputs: AudioDevice[] = devices
          .filter((device) => device.kind === 'audioinput')
          .map((device, index) => ({
            deviceId: device.deviceId,
            label: device.label || `Microphone ${index + 1}`,
            kind: 'audioinput' as const,
          }));

        setInputDevices(inputs);
        setIsLoading(false);
      } catch (err) {
        console.error('[useAudioDevices] Failed to enumerate devices:', err);
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to get audio devices');
          setIsLoading(false);
        }
      }
    };

    enumerateDevices();

    const handleDeviceChange = () => {
      enumerateDevices();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    return () => {
      mounted = false;
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [enabled]);

  return { inputDevices, isLoading, error };
}
